import { normalizeUserId } from "@/api/connectApi";

/**
 * @mentions in 1:1 and group chats. A picked person travels in the message
 * HTML as
 *
 *   <span data-fg-mention="<userId>" class="fg-mention">@Name</span>
 *
 * which DOMPurify keeps (span, class and data-* are all allowed), so the one
 * message pipeline — and Mail's chat dock — carries it without a schema
 * change. The BFF reads the attribute to notify the mentioned person (members
 * of the chat only). The name is escaped text; the id must be a user id.
 */
export interface MentionPick {
    userId: string;
    name: string;
}

export const MENTION_ATTR = "data-fg-mention";
/** Stand-in "person" for @everyone: the BFF notifies every member of the chat when it sees the attribute. */
export const EVERYONE_ID = "everyone";
export const MENTION_ALL_ATTR = "data-fg-mention-all";

const USER_ID = /^[0-9a-f]{32}$/;

function escapeText(value: string): string {
    return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function mentionHtml(pick: MentionPick): string {
    if (pick.userId === EVERYONE_ID) return `<span ${MENTION_ALL_ATTR}="1" class="fg-mention">@everyone</span>`;
    const id = normalizeUserId(pick.userId);
    if (!USER_ID.test(id)) return escapeText(`@${pick.name}`);
    return `<span ${MENTION_ATTR}="${id}" class="fg-mention">@${escapeText(pick.name)}</span>`;
}

// Private-use characters survive HTML escaping and every Markdown pattern.
const OPEN = String.fromCharCode(0xe000);
const CLOSE = String.fromCharCode(0xe001);
const PLACEHOLDER = new RegExp(`${OPEN}(\\d+)${CLOSE}`, "g");

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Render composer text to HTML with each picked person's "@Name" turned into
 * a mention chip. Picks whose "@Name" was deleted from the text are dropped.
 * The text is swapped for placeholders before `render` (Markdown) runs, so a
 * name with `_` or `*` in it can't be mangled, then the chips go in after.
 */
export function renderWithMentions(text: string, picks: MentionPick[], render: (text: string) => string): string {
    if (picks.length === 0 || !text.includes("@")) return render(text);
    // Longest names first, so "@Ann Lee" wins over "@Ann".
    const ordered = [...picks].sort((a, b) => b.name.length - a.name.length);
    let swapped = text;
    ordered.forEach((pick, i) => {
        // "@Name" at the start or after whitespace/punctuation, not mid-word or mid-email.
        const pattern = new RegExp(`(^|[^\\w@.])@${escapeRegExp(pick.name)}(?![\\w])`, "g");
        swapped = swapped.replace(pattern, `$1${OPEN}${i}${CLOSE}`);
    });
    return render(swapped).replace(PLACEHOLDER, (_m, n: string) => {
        const pick = ordered[Number(n)];
        return pick ? mentionHtml(pick) : "";
    });
}

/** Mark the mentions of `me` in already-sanitised HTML so they stand out. Returns the input when there are none. */
export function markMentionsOf(html: string, me: string): string {
    if (!me || !html.includes(MENTION_ATTR)) return html;
    // DOMParser documents are inert — nothing runs and nothing loads.
    const doc = new DOMParser().parseFromString(html, "text/html");
    let changed = false;
    for (const el of Array.from(doc.body.querySelectorAll(`[${MENTION_ATTR}]`))) {
        if (normalizeUserId(el.getAttribute(MENTION_ATTR)) === me) {
            el.classList.add("fg-mention-me");
            changed = true;
        }
    }
    return changed ? doc.body.innerHTML : html;
}
